// The two export flavors: the EU DDS file per "EUDR GeoJson File Description" v1.5, and the Angel-to-Angel share file.
import {
  closeRing,
  ringIsClosed,
  dedupeConsecutive,
  ringIsDegenerate,
  ringIsZeroArea,
  ringIsUndersized,
  ringSelfIntersects,
  simplifyRing,
  countFeatureVertices,
  checkCoordinates,
  pointInGeometry,
  multiPolygonPartsTouch,
  ACCEPTED_GEOMETRY_TYPES
} from '@/utils/eudr/EudrGeometry.js'
import { featureDisplayName } from '@/utils/eudr/EudrFeatureName.js'
import { splitPolygonHoles } from '@/utils/eudr/EudrHoleSplit.js'
import cloneDeep from 'lodash/cloneDeep'

export const MAX_FEATURE_VERTICES = 500
export const MAX_FILE_BYTES = 25 * 1024 * 1024 // the EU DDS upload limit
const SIMPLIFY_BASE_EPSILON = 0.000002 // ~0.2m; doubled per pass until the budget is met
const MAX_SIMPLIFY_PASSES = 16

// Report display only — the exported EU file keeps the supplier's original ProductionPlace as its reconciliation ID.
function featureName (feature) {
  return featureDisplayName(feature)
}

// Holes are split, not stripped (the EU's own half-moon workaround), so the hole's ground stays out of the declaration.
function repairGeometry (geometry, report) {
  if (!geometry) return null
  if (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon') return geometry

  const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates
  const simplePolygons = []
  for (const polygon of polygons ?? []) {
    if (!polygon?.length) continue
    if (polygon.length > 1) {
      const holeCount = polygon.length - 1
      const pieces = splitPolygonHoles(polygon)
      if (pieces) {
        report.holesSplit += holeCount
        simplePolygons.push(...pieces)
      } else {
        report.holesStripped += holeCount
        simplePolygons.push([polygon[0]])
      }
      continue
    }
    simplePolygons.push(polygon)
  }

  const repairedPolygons = []
  for (const polygon of simplePolygons) {
    let ring = polygon[0]
    if (!ringIsClosed(ring)) {
      report.ringsClosed++
      ring = closeRing(ring)
    }
    const { ring: deduped, removed } = dedupeConsecutive(ring)
    report.duplicatePointsRemoved += removed
    // Clipping can leave sliver pieces; skip those rather than failing the whole feature.
    if (ringIsDegenerate(deduped) || ringIsZeroArea(deduped)) continue
    if (ringIsUndersized(deduped)) {
      report.undersizedPartsRemoved++
      continue
    }
    repairedPolygons.push([deduped])
  }
  if (repairedPolygons.length === 0) return null

  return repairedPolygons.length === 1
    ? { type: 'Polygon', coordinates: repairedPolygons[0] }
    : { type: 'MultiPolygon', coordinates: repairedPolygons }
}

function mapRings (geometry, fn) {
  if (geometry.type === 'Polygon') {
    return { type: 'Polygon', coordinates: geometry.coordinates.map(fn) }
  }
  if (geometry.type === 'MultiPolygon') {
    return { type: 'MultiPolygon', coordinates: geometry.coordinates.map(polygon => polygon.map(fn)) }
  }
  return geometry
}

function anyRingSelfIntersects (geometry) {
  if (geometry.type === 'Polygon') return geometry.coordinates.some(ringSelfIntersects)
  if (geometry.type === 'MultiPolygon') return geometry.coordinates.some(polygon => polygon.some(ringSelfIntersects))
  return false
}

// What the EU validator reports as a self-intersecting polygon: a ring crossing itself, or (for
// MultiPolygons) parts that touch or overlap each other — hole-split output is the classic source.
function readsAsSelfIntersecting (geometry) {
  return anyRingSelfIntersects(geometry) || multiPolygonPartsTouch(geometry)
}

// Backs off if a pass introduces a self-intersection (Douglas–Peucker can create one on gnarly shapes).
function simplifyFeatureGeometry (feature, maxVertices, report) {
  let epsilon = SIMPLIFY_BASE_EPSILON
  let best = feature.geometry
  for (let pass = 0; pass < MAX_SIMPLIFY_PASSES && countVertices(best) > maxVertices; pass++) {
    const candidate = mapRings(best, ring => dedupeConsecutive(simplifyRing(ring, epsilon)).ring)
    if (readsAsSelfIntersecting(candidate)) break
    best = candidate
    epsilon *= 2
  }
  if (best !== feature.geometry) {
    report.simplifiedFeatures++
    feature.geometry = best
  }
}

function countVertices (geometry) {
  return countFeatureVertices({ geometry })
}

// Input must already be rounded to 6 decimals; report.blocking lists reasons the file must not be submitted yet.
export function sanitizeForSubmission (featureCollection, excludedIds = new Set()) {
  const report = {
    totalFeatures: featureCollection?.features?.length ?? 0,
    includedFeatures: 0,
    excludedFeatures: 0,
    holesSplit: 0,
    holesStripped: 0,
    ringsClosed: 0,
    duplicatePointsRemoved: 0,
    simplifiedFeatures: 0,
    verticesBefore: 0,
    verticesAfter: 0,
    maxFeatureVertices: 0,
    selfIntersecting: [],
    invalidFeatures: [],
    undersizedPartsRemoved: 0,
    undersizedFeatures: [],
    nonNumericAreas: 0,
    invalidCountries: 0,
    pointsInsidePolygons: 0,
    pointsOutsidePolygons: 0,
    sizeBytes: 0,
    sizeOk: true
  }

  // Points are legal for plots under 4 ha (TRACES imputes an area-less point); these counts warn, never block.
  const included = (featureCollection?.features ?? []).filter(f => !excludedIds.has(f.properties?.featureId))
  const includedPolygonal = included.filter(f => ['Polygon', 'MultiPolygon'].includes(f.geometry?.type))
  for (const feature of included) {
    if (feature.geometry?.type !== 'Point') continue
    const [lon, lat] = feature.geometry.coordinates ?? []
    const covered = Number.isFinite(lon) && Number.isFinite(lat) &&
      includedPolygonal.some(p => pointInGeometry(lon, lat, p.geometry))
    if (covered) report.pointsInsidePolygons++
    else report.pointsOutsidePolygons++
  }

  const outFeatures = []
  for (const feature of featureCollection?.features ?? []) {
    const props = feature.properties ?? {}
    if (excludedIds.has(props.featureId)) {
      report.excludedFeatures++
      continue
    }

    // Same predicates as EudrGeometry.analyzeFeature, so these counts match the per-feature warnings shown in review.
    const area = props.area ?? props.Area
    if (area != null && typeof area !== 'number') report.nonNumericAreas++
    const country = props.producerCountry ?? props.ProducerCountry
    if (country != null && !/^[A-Z]{2}$/.test(String(country))) report.invalidCountries++

    // Unrepairable without guessing (out-of-range positions are often swapped lon/lat); these block the submission.
    if (!feature.geometry || !ACCEPTED_GEOMETRY_TYPES.includes(feature.geometry.type) || checkCoordinates(feature.geometry)) {
      report.invalidFeatures.push(featureName(feature))
      continue
    }

    const undersizedBefore = report.undersizedPartsRemoved
    const geometry = repairGeometry(cloneDeep(feature.geometry), report)
    if (!geometry) {
      // Nothing left but pieces of 0.01 ha or less: dropped from the file (an Osapiens rule), not blocking.
      if (report.undersizedPartsRemoved > undersizedBefore) {
        report.undersizedPartsRemoved = undersizedBefore
        report.undersizedFeatures.push(featureName(feature))
      } else {
        report.invalidFeatures.push(featureName(feature))
      }
      continue
    }

    // Counted only for features that make it into the file, so the report's before → after compares like with like.
    report.verticesBefore += countFeatureVertices(feature)

    // ProducerCountry is hardcoded US for now; the rest of the producer identity is supplied at DDS
    // submission time, and the share file keeps provenance.
    const outFeature = { type: 'Feature', properties: { ProducerCountry: 'US' }, geometry }
    simplifyFeatureGeometry(outFeature, MAX_FEATURE_VERTICES, report)

    if (readsAsSelfIntersecting(outFeature.geometry)) {
      report.selfIntersecting.push(featureName(feature))
    }

    report.includedFeatures++
    outFeatures.push(outFeature)
  }

  const geojson = { type: 'FeatureCollection', features: outFeatures }
  let serialized = JSON.stringify(geojson)
  // Exact UTF-8 byte count — string .length counts UTF-16 code units and understates multi-byte characters.
  const byteSize = (s) => new Blob([s]).size

  // Over the DDS limit: progressively simplify every polygon until it fits or passes run out.
  let epsilon = SIMPLIFY_BASE_EPSILON * 8
  for (let pass = 0; pass < MAX_SIMPLIFY_PASSES && byteSize(serialized) > MAX_FILE_BYTES; pass++) {
    for (const feature of outFeatures) {
      const candidate = mapRings(feature.geometry, ring => dedupeConsecutive(simplifyRing(ring, epsilon)).ring)
      if (!readsAsSelfIntersecting(candidate)) feature.geometry = candidate
    }
    epsilon *= 2
    serialized = JSON.stringify(geojson)
  }
  report.sizeBytes = byteSize(serialized)
  report.sizeOk = report.sizeBytes <= MAX_FILE_BYTES
  report.verticesAfter = outFeatures.reduce((sum, f) => sum + countFeatureVertices(f), 0)
  report.maxFeatureVertices = outFeatures.reduce((max, f) => Math.max(max, countFeatureVertices(f)), 0)

  report.blocking = report.selfIntersecting.length > 0 || report.invalidFeatures.length > 0 || !report.sizeOk

  return { serialized, report }
}

// Dropped from the share file; parcelId deliberately stays so the file remains composite-eligible for the receiver.
const SHARE_STRIPPED_PROPS = ['featureId', 'excluded', 'conflict', 'accountId', 'sourcePoint']

export function buildShareFile (featureCollection, excludedIds = new Set()) {
  const features = []
  for (const feature of featureCollection?.features ?? []) {
    const props = feature.properties ?? {}
    if (excludedIds.has(props.featureId)) continue
    const outProps = { ...props }
    for (const key of SHARE_STRIPPED_PROPS) delete outProps[key]
    features.push({ type: 'Feature', properties: outProps, geometry: cloneDeep(feature.geometry) })
  }
  return {
    type: 'FeatureCollection',
    angelEudrSchema: 2,
    generatedAt: new Date().toISOString(),
    features
  }
}
